import React from 'react';
import { ImageStyle, Pressable } from 'react-native';
import { useDispatch } from 'react-redux';
import { PresentedMeta } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNAConditionalWrapper,
  DNAContextMenu,
  DNAText,
  Iffy,
  Stack,
} from '@alucio/lux-ui';
import { DNAFileTypeChip, DNAPopover, DNAThumbnail } from 'src/components/DNA';
import { PresentablePage } from 'src/types/types';
import { detectArchivedFileKeyPath } from '../../SlideSelector/useThumbnailSelector';
import {
  SLIDE_ROLL_VARIANT,
  ThumbnailNumberPosition,
  thumbnailNumberPositionProps,
  thumbnailVariantSettings,
  thumbnailVariantStyles,
} from './StyleSetting';
import ItemWrapper from 'src/components/DNA/OldSlideRoll/Wrapper/Wrapper';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import SlidePreviewModal from '../SlidePreview/SlidePreviewModal';
import { useSlideRollState } from './SlideRollProvider';
import {
  canDirectSelectPresentationPage,
  canToggleHiddenSlide,
} from 'src/utils/hiddenSlides';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useAppSettings } from 'src/state/context/AppSettings';
import { getHiddenSlideTooltip, shouldShowHiddenSlideTooltip } from './hiddenSlideTooltip';
import { thumbnailTintOverlayStyle } from './thumbnailTintOverlayStyle';

interface DNAPresentableThumbnailProps {
  fileTypeVisible?: boolean,
  hasNotation?: boolean,
  isContentUnavailable?: boolean,
  isSelected?: boolean,
  tintOverlay?: boolean,
  numberPosition?: ThumbnailNumberPosition,
  page: PresentablePage,
  presentedMeta?: PresentedMeta,
  title?: string,
}

const DNASlideRollThumbnail: React.FC<DNAPresentableThumbnailProps> = ({
  isContentUnavailable,
  isSelected,
  numberPosition,
  page,
  presentedMeta,
  tintOverlay,
  fileTypeVisible,
  title,
  hasNotation,
}) => {
  const {
    displayPreviewBtn,
    variant,
    onSelectPage,
    onToggleHidden,
    isTablet,
    itemHeight,
    itemWidth,
  } = useSlideRollState();
  const s3URL = detectArchivedFileKeyPath(page.documentVersionORM.model, page.page, isTablet ? 'sm' : 'xl');
  const variantStyles = thumbnailVariantStyles[variant];
  const variantSettings = thumbnailVariantSettings[variant];
  const thumbnailPlacementProps = thumbnailNumberPositionProps[numberPosition ?? variantSettings.numberPosition];
  const titleWidth = (itemWidth ?? variantSettings.thumbnailWidth);
  const bypassItemWrapper = variant !== SLIDE_ROLL_VARIANT.MEETINGS;
  const itemWrapperHeight = Number(variantStyles.pressableContainer.height || variantSettings.thumbnailHeight || 0) + 2;
  const thumbnailHeight = itemHeight ?? variantSettings.thumbnailHeight;
  const thumbnailWidth = itemWidth ?? variantSettings.thumbnailWidth;
  const dispatch = useDispatch();
  const { deviceMode } = useAppSettings();
  const enableHiddenSlides = useFeatureFlags('BEAC_7666_enable_hidden_slides');
  const canToggleHidden = !!onToggleHidden && (canToggleHiddenSlide(page.page) || !!page.page.isHidden);
  const canPresentSlide = canDirectSelectPresentationPage({
    featureEnabled: enableHiddenSlides,
    page: page.page,
  });
  const toggleHiddenTitle = page.page.isHidden ? 'Unhide slide' : 'Hide slide';
  const hiddenSlideTooltip = getHiddenSlideTooltip(page.page);
  const shouldShowTooltip = shouldShowHiddenSlideTooltip({
    deviceMode,
    ...page.page,
  });
  const handleToggleHiddenPress = () => {
    onToggleHidden?.(page);
  };

  if (isContentUnavailable) {
    return (
      <DNABox style={[variantStyles.container, { margin: 3 }]} />
    );
  }

  const onPreview = (): void => {
    const previewS3URL = detectArchivedFileKeyPath(page.documentVersionORM.model, page.page, 'xl');
    dispatch(
      DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (props) => (<SlidePreviewModal
          presentPage={() => onSelectPage(page.presentationPageNumber)}
          archivedKey={previewS3URL || ''}
          title={title || ''}
          {...props}
        />),
      }))
  }

  return (
    <ItemWrapper
      bypassWrapper={bypassItemWrapper}
      itemHeight={itemWrapperHeight}
      onPresent={canPresentSlide ? () => onSelectPage(page.presentationPageNumber) : undefined}
      onPreview={onPreview}
      showPresent={canPresentSlide}
      showPreview={displayPreviewBtn}
    >
      <DNAConditionalWrapper
        condition={shouldShowTooltip}
        wrapper={children => (
          <DNAPopover placement="top">
            <DNAPopover.Anchor>{children}</DNAPopover.Anchor>
            <DNAPopover.Content>
              <DNAText colorMode="dark">{hiddenSlideTooltip}</DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        )}
      >
        <Pressable
          onPress={canPresentSlide ? () => onSelectPage(page.presentationPageNumber) : undefined}
          style={variantStyles.pressableContainer}
        >
          {/* CONTAINER  */}
          <DNABox testID={`slide-roll-image-${page.presentationPageNumber}`}{...thumbnailPlacementProps} spacing="xs">
            <Stack anchor="center">
              <Stack.Layer>
                {/* OUTER THUMBNAIL CONTAINER */}
                <DNABox
                  key={page.id}
                  style={[
                    variantStyles.container,
                    { position: 'relative' },
                    isSelected && variantStyles.selected,
                  ]}
                  alignX="center"
                  alignY="center"
                >
                  {/* INNER THUMBNAIL CONTAINER */}
                  <DNABox
                    alignX="center"
                    alignY="center"
                    style={[
                      variantStyles.innerContainer,
                      !isSelected
                        ? variantStyles.unselected
                        : variantStyles.selected,
                    ]}
                  >
                    {/* THUMBNAIL */}
                    <DNAThumbnail
                      style={variantStyles.thumbnail as ImageStyle}
                      s3URL={s3URL}
                      presentedMeta={presentedMeta}
                      height={thumbnailHeight}
                      width={thumbnailWidth}
                      hasNotation={hasNotation}
                      isHidden={page.page.isHidden}
                      sourceIsHidden={page.page.sourceIsHidden && page.page.isHidden}
                    />
                  </DNABox>
                  <Iffy is={tintOverlay && !presentedMeta?.presented}>
                    <DNABox
                      pointerEvents="none"
                      style={thumbnailTintOverlayStyle}
                    />
                  </Iffy>
                </DNABox>
              </Stack.Layer>
              <Stack.Layer anchor="topRight" style={{ marginTop: 6, marginRight: 6 }}>
                <Iffy is={canToggleHidden}>
                  <DNAContextMenu placement="bottom end">
                    <DNAContextMenu.Anchor>
                      <DNAButton
                        status="transparent"
                        iconLeft="dots-three-vertical"
                      />
                    </DNAContextMenu.Anchor>
                    <DNAContextMenu.Items>
                      <DNAContextMenu.Item
                        title={toggleHiddenTitle}
                        icon={page.page.isHidden ? 'eye' : 'prohibit'}
                        onPress={handleToggleHiddenPress}
                      />
                    </DNAContextMenu.Items>
                  </DNAContextMenu>
                </Iffy>
              </Stack.Layer>
            </Stack>
            <DNABox spacing="between">

              {/* PAGE NUMBER */}
              <DNAText
                testID="slide-thumbnail-page-number"
                numberOfLines={1}
                style={[
                  { ...variantStyles.pageText, maxWidth: titleWidth },
                  isSelected && variantStyles.pageTextSelected,
                ]}
              >
                {`${page.presentationPageNumber}. ${title}` }
              </DNAText>

              {/* Badge */}
              {fileTypeVisible && <DNAFileTypeChip orm={page.documentVersionORM} variant="THUMBNAIL" />}
            </DNABox>
          </DNABox>
        </Pressable>
      </DNAConditionalWrapper>
    </ItemWrapper>
  );
};

DNASlideRollThumbnail.displayName = 'DNASlideRollThumbnail';

export default DNASlideRollThumbnail;
